import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useMemo, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import $livequeueChats from '@store/livequeueChatStore';
import { ChatMessageComponent } from '../ChatMessage/ChatMessage';
import ProviderResultsServiceMessage from '../ChatMessage/ChatMessageProvider/ui/ProviderResultsServiceMessage/ProviderResultsServiceMessage';
import { SystemMessage } from '../ChatMessage/SystemMessage/SystemMessage';
import { UnreadLine } from '../ChatMessage/SystemMessage/ui/UnreadLine/UnreadLine';
import styles from './styles.module.scss';
export const MessagesList = ({ role, currentRoleId, messages, consultation, isInitialLoading, handleCheckResultsForAttachments, handleShowResultsModal, onAttachmentClick, containerRef, }) => {
    var _a;
    const { t } = useTranslation();
    const isClient = role === 'client';
    const isProvider = role === 'provider';
    const unreadAnchorId = $livequeueChats.unreadAnchorId;
    const unreadAnchorIndex = unreadAnchorId
        ? messages.findIndex((m) => m._id === unreadAnchorId)
        : -1;
    const unreadLineRef = useRef(null);
    const hideAboveTimerRef = useRef(null);
    const hideBottomTimerRef = useRef(null);
    useEffect(() => {
        if (!unreadAnchorId) {
            $livequeueChats.setUnreadAnchorVisible(false);
            if (hideAboveTimerRef.current) {
                clearTimeout(hideAboveTimerRef.current);
                hideAboveTimerRef.current = null;
            }
            if (hideBottomTimerRef.current) {
                clearTimeout(hideBottomTimerRef.current);
                hideBottomTimerRef.current = null;
            }
            return;
        }
        const marker = unreadLineRef.current;
        const root = containerRef.current;
        if (!marker || !root) {
            $livequeueChats.setUnreadAnchorVisible(false);
            return;
        }
        const handleScroll = () => {
            const containerEl = root;
            if (containerEl) {
                const distanceFromBottom = containerEl.scrollHeight - containerEl.scrollTop - containerEl.clientHeight;
                const atBottom = distanceFromBottom <= 4;
                if (atBottom) {
                    if (!hideBottomTimerRef.current) {
                        hideBottomTimerRef.current = setTimeout(() => {
                            $livequeueChats.clearUnreadState();
                            hideBottomTimerRef.current = null;
                        }, 5000);
                    }
                }
                else if (hideBottomTimerRef.current) {
                    clearTimeout(hideBottomTimerRef.current);
                    hideBottomTimerRef.current = null;
                }
            }
        };
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                if (hideAboveTimerRef.current) {
                    clearTimeout(hideAboveTimerRef.current);
                    hideAboveTimerRef.current = null;
                }
                $livequeueChats.setUnreadAnchorVisible(true);
                handleScroll();
                root.addEventListener('scroll', handleScroll);
                return;
            }
            root.removeEventListener('scroll', handleScroll);
            const containerRect = (root === null || root === void 0 ? void 0 : root.getBoundingClientRect()) || {
                top: 0,
                bottom: window.innerHeight,
            };
            const elRect = entry.boundingClientRect;
            const wentAbove = elRect.bottom <= containerRect.top;
            const wentBelow = elRect.top >= containerRect.bottom;
            if (wentBelow) {
                return;
            }
            if (wentAbove) {
                if (!hideAboveTimerRef.current) {
                    hideAboveTimerRef.current = setTimeout(() => {
                        $livequeueChats.clearUnreadState();
                        hideAboveTimerRef.current = null;
                    }, 5000);
                }
            }
        }, { root, threshold: 0.1 });
        observer.observe(marker);
        return () => {
            root.removeEventListener('scroll', handleScroll);
            if (hideAboveTimerRef.current) {
                clearTimeout(hideAboveTimerRef.current);
                hideAboveTimerRef.current = null;
            }
            if (hideBottomTimerRef.current) {
                clearTimeout(hideBottomTimerRef.current);
                hideBottomTimerRef.current = null;
            }
            observer.disconnect();
        };
    }, [unreadAnchorId, containerRef]);
    const unreadCount = (_a = $livequeueChats.subscriptions.find((s) => s.chat_id === consultation.chat_id)) === null || _a === void 0 ? void 0 : _a.unread_count;
    const formatDate = (date) => {
        return new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric' }).format(new Date(date));
    };
    const renderedMessages = useMemo(() => {
        return messages.flatMap((msg, idx) => {
            const prevMessage = idx > 0 ? messages[idx - 1] : null;
            const isSameSender = prevMessage && prevMessage.role_id === msg.role_id;
            const isLabResults = msg.attachments.length > 0 && msg.attachments.some((el) => el.type === 'lab_results');
            let showDateSeparator = false;
            if (!prevMessage) {
                showDateSeparator = true;
            }
            else {
                const prevMessageDate = new Date(prevMessage.created).toDateString();
                const currentMessageDate = new Date(msg.created).toDateString();
                if (prevMessageDate !== currentMessageDate) {
                    showDateSeparator = true;
                }
            }
            if (isProvider && !prevMessage) {
                showDateSeparator = false;
            }
            const isUnread = unreadCount > 0 && idx === messages.length - unreadCount;
            const showNewLine = idx === unreadAnchorIndex && unreadAnchorIndex !== -1 && msg.role_id !== currentRoleId;
            const nodes = [];
            if (showDateSeparator) {
                nodes.push(_jsx(SystemMessage, { message: formatDate(msg.created), showUnreadLine: isUnread }, `${msg._id}-date`));
            }
            if (showNewLine) {
                nodes.push(_jsx(UnreadLine, { anchorRef: unreadLineRef, className: styles.unreadLine }, `${msg._id}-new`));
            }
            nodes.push(_jsxs("div", Object.assign({ id: msg._id, className: cn(styles.msgWrapper, isSameSender && styles.messageWithSmallMargin) }, { children: [_jsx(ChatMessageComponent, { message: msg, role: role, showTimestamp: !isClient, onActionClick: handleShowResultsModal, onAttachmentClick: onAttachmentClick, consultation: consultation }), isProvider && isLabResults && (_jsx(ProviderResultsServiceMessage, { consultation: consultation, role: role, message: msg, onCheckResults: () => handleCheckResultsForAttachments(consultation, msg.attachments) }))] }), msg._id));
            return nodes;
        });
    }, [
        messages,
        unreadAnchorIndex,
        currentRoleId,
        isClient,
        isProvider,
        handleShowResultsModal,
        onAttachmentClick,
        handleCheckResultsForAttachments,
        consultation,
        unreadCount,
    ]);
    return _jsx("div", Object.assign({ className: styles.messagesInner }, { children: renderedMessages }));
};
export default observer(MessagesList);
